import type { Role } from './role';

/**
 * The lab geofence, set by Management.
 *
 * A station sign in is allowed only inside the lab radius. The product has
 * promised this on the login screen since the prototype; until now the pill
 * was decorative and nothing was checked.
 *
 * READ THIS BEFORE RELYING ON IT. This is a frontend check and a frontend
 * check is advisory. A browser can be told to report any coordinates at all,
 * so this stops somebody signing in from the car park by accident, not
 * somebody determined to. Real enforcement belongs on the server, which sees
 * the request IP and can refuse the token. That is written up for the backend
 * developer in the handoff.
 */
export interface LabGeofence {
  enabled: boolean;
  latitude: number;
  longitude: number;
  radiusMetres: number;
  /**
   * Which roles must be inside the radius.
   *
   * Defaults to the two that sign in at a station on the floor. Management,
   * Reception and Accounts work at desks and travel, and locking a manager out
   * of their own system because they are at the dentist's office would be a
   * worse failure than the one this prevents.
   */
  appliedRoles: Role[];
}

export const DEFAULT_GEOFENCE: LabGeofence = {
  enabled: false,
  // Ceramica Dental Lab, Dubai. Replaced by the manager on the admin screen.
  latitude: 25.2048,
  longitude: 55.2708,
  radiusMetres: 300,
  appliedRoles: ['tech', 'cadcam'],
};

const EARTH_RADIUS_METRES = 6_371_000;

function toRadians(degrees: number): number {
  return (degrees * Math.PI) / 180;
}

/**
 * Great circle distance in metres, by the haversine formula.
 *
 * Haversine rather than the flat approximation because the flat one drifts
 * with latitude, and a radius measured in hundreds of metres cannot absorb
 * that. It is also stable for two points a few metres apart, which is the case
 * that matters here.
 */
export function distanceMetres(
  from: { latitude: number; longitude: number },
  to: { latitude: number; longitude: number },
): number {
  const deltaLat = toRadians(to.latitude - from.latitude);
  const deltaLon = toRadians(to.longitude - from.longitude);
  const fromLat = toRadians(from.latitude);
  const toLat = toRadians(to.latitude);

  const a =
    Math.sin(deltaLat / 2) ** 2 +
    Math.sin(deltaLon / 2) ** 2 * Math.cos(fromLat) * Math.cos(toLat);

  return 2 * EARTH_RADIUS_METRES * Math.asin(Math.min(1, Math.sqrt(a)));
}

export type GeofenceVerdict =
  | { allowed: true; reason: 'not-enforced' | 'inside' }
  | { allowed: false; reason: 'outside'; metresAway: number }
  | { allowed: false; reason: 'unavailable' };

/**
 * Whether this sign in may proceed.
 *
 * `position` is null when the browser refused or could not answer. That is a
 * refusal, not a pass: a geofence that waves through anyone who blocks the
 * permission prompt is not a geofence. It is only reached for a role the fence
 * applies to, so a manager with location switched off is unaffected.
 */
export function checkGeofence(
  fence: LabGeofence,
  role: Role,
  position: { latitude: number; longitude: number } | null,
): GeofenceVerdict {
  if (!fence.enabled) return { allowed: true, reason: 'not-enforced' };
  if (!fence.appliedRoles.includes(role)) return { allowed: true, reason: 'not-enforced' };
  if (position === null) return { allowed: false, reason: 'unavailable' };

  const metres = distanceMetres(fence, position);
  if (metres <= fence.radiusMetres) return { allowed: true, reason: 'inside' };

  return { allowed: false, reason: 'outside', metresAway: Math.round(metres) };
}

/**
 * How the log words the location a sign in gave. Section 28 of the review of
 * 2 October 2026: the browser decides, the server records what it was told,
 * measured against the fence the server holds. The API words the row the same
 * way (SignInLocation.Describe in Dentist.Domain/Ceramica).
 */
export function signInLocationAction(
  fence: LabGeofence,
  position: { latitude: number; longitude: number } | null,
): string {
  if (position === null) return 'gave no location at sign in';

  const metres = Math.round(distanceMetres(fence, position));
  const away = metres < 1000 ? `${metres} m` : `${(metres / 1000).toFixed(1)} km`;
  const side = metres <= fence.radiusMetres ? 'inside' : 'outside';
  return `signed in ${away} from the lab, ${side} the ${fence.radiusMetres} m fence`;
}
